import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  Menu,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconDotsVertical, IconPencil, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { describeRpcError, usePermission } from "@how/app-kit";
import {
  ConfirmActionModal,
  ErrorState,
  LoadingState,
  RecordDrawer,
  formatDateOnly,
} from "@how/ui";
import { usePeopleOptions } from "../people/household-queries";
import { GroupModal } from "./GroupModal";
import {
  DAY_LABEL,
  JOINING_LABEL,
  VISIBILITY_LABEL,
  formatMeetingTime,
  memberName,
  useAddGroupMember,
  useDecideGroupRequest,
  useGroupRoster,
  useGroupSeasons,
  useGroups,
  useMyLedGroupIds,
  useRemoveGroupMember,
  useSaveSeason,
  useSetGroupMemberRole,
  type GroupRow,
  type RosterMember,
  type SeasonFields,
} from "./group-queries";
import { semanticColor } from "@how/design-system";

export interface GroupDrawerProps {
  readonly groupId: string | null;
  readonly onClose: () => void;
}

const EMPTY_SEASON: SeasonFields = { name: "", startsOn: "", endsOn: "", isCurrent: true };

/**
 * One group: what it is, its seasons, and the roster for the season on screen — leaders, members, who is waiting,
 * and who left. Editing takes groups.manage, or groups.manage_assigned when the caller leads this group; the
 * database decides, this only decides what to draw. Names come from group_roster, so a child appears only to a
 * caller with people.read_minors and everyone else is told how many are not shown (ADR 0028).
 */
export function GroupDrawer({ groupId, onClose }: GroupDrawerProps) {
  // The row from the list (already loaded) rather than a second read of the group.
  const groups = useGroups({ search: "", status: "all" });
  const group: GroupRow | undefined = groups.data?.find((g) => g.id === groupId);
  const seasons = useGroupSeasons(groupId);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const roster = useGroupRoster(groupId, seasonId);
  const canManage = usePermission("groups.manage", { write: true });
  const canManageAssigned = usePermission("groups.manage_assigned", { write: true });
  const canPickPeople = usePermission("people.read");
  const led = useMyLedGroupIds();
  const manage =
    canManage.data === true ||
    (canManageAssigned.data === true && groupId !== null && (led.data ?? []).includes(groupId));

  const addMember = useAddGroupMember();
  const setRole = useSetGroupMemberRole();
  const removeMember = useRemoveGroupMember();
  const decide = useDecideGroupRequest();
  const saveSeason = useSaveSeason();

  const [editing, setEditing] = useState(false);
  const [addingSeason, setAddingSeason] = useState(false);
  const [seasonFields, setSeasonFields] = useState<SeasonFields>(EMPTY_SEASON);
  const [search, setSearch] = useState("");
  const [newMember, setNewMember] = useState<string | null>(null);
  const [newLeader, setNewLeader] = useState(false);
  const [removing, setRemoving] = useState<RosterMember | null>(null);

  const rosterData = roster.data;
  const members = rosterData?.members ?? [];
  const options = usePeopleOptions(
    search,
    members.filter((m) => m.status === "active" || m.status === "requested").map((m) => m.personId),
  );
  const currentSeason = roster.data?.season;
  const requests = members.filter((m) => m.status === "requested");
  const leaders = members.filter((m) => m.status === "active" && m.role === "leader");
  const active = members.filter((m) => m.status === "active" && m.role === "member");
  const gone = members.filter((m) => m.status === "left" || m.status === "declined");

  const close = () => {
    setEditing(false);
    setAddingSeason(false);
    setSeasonId(null);
    setSearch("");
    setNewMember(null);
    onClose();
  };

  const rosterError = roster.isError ? describeRpcError(roster.error) : null;

  return (
    <RecordDrawer
      opened={groupId !== null}
      onClose={close}
      title={group?.name ?? "Group"}
      subtitle={
        group === undefined ? undefined : (
          <Group gap="xs">
            <Badge variant="light" color={group.isActive ? "teal" : "gray"}>
              {group.isActive ? "Active" : "Archived"}
            </Badge>
            <Text size="sm" c="dimmed">
              {[group.categoryName, group.campusName].filter((p) => p !== null).join(" · ")}
            </Text>
          </Group>
        )
      }
    >
      {group === undefined ? (
        groups.isPending ? (
          <LoadingState rows={4} />
        ) : (
          <ErrorState message="This group is not one you can see." />
        )
      ) : (
        <Stack gap="lg" mt="md">
          <section>
            <Group justify="space-between" align="center">
              <Title order={4}>About</Title>
              {manage ? (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </Group>
            {group.description === null ? (
              <Text size="sm" c="dimmed" mt="xs">
                No description yet. Members read it before they join.
              </Text>
            ) : (
              <Text size="sm" mt="xs" style={{ whiteSpace: "pre-wrap" }}>
                {group.description}
              </Text>
            )}
            <SimpleGrid cols={2} spacing="xs" mt="sm">
              <Fact
                label="Meets"
                value={
                  group.meetingDay === null && group.meetingTime === null
                    ? null
                    : [
                        group.meetingDay === null ? null : DAY_LABEL[group.meetingDay],
                        formatMeetingTime(group.meetingTime),
                      ]
                        .filter((p): p is string => p !== null)
                        .join(" at ")
                }
              />
              <Fact label="Where" value={group.meetingLocation} />
              <Fact label="Who can see it" value={VISIBILITY_LABEL[group.visibility]} />
              <Fact label="How people join" value={JOINING_LABEL[group.joiningRule]} />
              <Fact
                label="Capacity"
                value={group.capacity === null ? "No limit" : String(group.capacity)}
              />
              <Fact
                label="Ages"
                value={
                  group.ageMin === null && group.ageMax === null
                    ? "Any"
                    : `${group.ageMin === null ? "" : String(group.ageMin)}–${group.ageMax === null ? "" : String(group.ageMax)}`
                }
              />
            </SimpleGrid>
          </section>

          <section>
            <Group justify="space-between" align="center">
              <Title order={4}>Season</Title>
              {manage ? (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => {
                    setSeasonFields(EMPTY_SEASON);
                    setAddingSeason(true);
                  }}
                >
                  Add a season
                </Button>
              ) : null}
            </Group>
            <Select
              aria-label="Season"
              mt="xs"
              data={(seasons.data ?? []).map((s) => ({
                value: s.id,
                label: `${s.name}${s.isCurrent ? " (current)" : ""} · from ${formatDateOnly(s.startsOn)}${s.endsOn === null ? "" : ` to ${formatDateOnly(s.endsOn)}`}`,
              }))}
              value={seasonId ?? currentSeason?.id ?? null}
              allowDeselect={false}
              onChange={(value) => {
                setSeasonId(value);
              }}
            />
            {addingSeason ? (
              <Stack gap="xs" mt="sm">
                <TextInput
                  label="Name"
                  placeholder="Fall 2026"
                  value={seasonFields.name}
                  onChange={(event) => {
                    const name = event.currentTarget.value;
                    setSeasonFields((f) => ({ ...f, name }));
                  }}
                />
                <Group grow>
                  <TextInput
                    label="Starts"
                    type="date"
                    value={seasonFields.startsOn}
                    onChange={(event) => {
                      const startsOn = event.currentTarget.value;
                      setSeasonFields((f) => ({ ...f, startsOn }));
                    }}
                  />
                  <TextInput
                    label="Ends"
                    type="date"
                    description="Leave empty for a group that keeps going."
                    value={seasonFields.endsOn}
                    onChange={(event) => {
                      const endsOn = event.currentTarget.value;
                      setSeasonFields((f) => ({ ...f, endsOn }));
                    }}
                  />
                </Group>
                <Checkbox
                  label="Make this the current season"
                  description="New members join the current season. The old roster stays where it was."
                  checked={seasonFields.isCurrent}
                  onChange={(event) => {
                    const isCurrent = event.currentTarget.checked;
                    setSeasonFields((f) => ({ ...f, isCurrent }));
                  }}
                />
                {saveSeason.isError ? (
                  <Text size="sm" c="red">
                    {describeRpcError(saveSeason.error).kind === "invalid"
                      ? "Check the dates: a season cannot end before it starts."
                      : "The season was not saved. Check your access and try again."}
                  </Text>
                ) : null}
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => {
                      setAddingSeason(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={seasonFields.name.trim().length === 0 || seasonFields.startsOn === ""}
                    loading={saveSeason.isPending}
                    onClick={() => {
                      saveSeason.mutate(
                        { groupId: group.id, seasonId: null, fields: seasonFields },
                        {
                          onSuccess: (id) => {
                            setAddingSeason(false);
                            setSeasonId(id);
                          },
                        },
                      );
                    }}
                  >
                    Add season
                  </Button>
                </Group>
              </Stack>
            ) : null}
          </section>

          <section>
            <Title order={4}>Roster</Title>
            {roster.isPending ? (
              <LoadingState rows={3} />
            ) : rosterError !== null ? (
              rosterError.kind === "denied" ? (
                <Text size="sm" c="dimmed" mt="xs">
                  You can see this group, but its roster is for its leaders and the office.
                </Text>
              ) : (
                <ErrorState
                  message="Could not load the roster."
                  onRetry={() => {
                    void roster.refetch();
                  }}
                />
              )
            ) : (
              <Stack gap="md" mt="xs">
                {requests.length > 0 ? (
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>
                      Waiting to join
                    </Text>
                    {requests.map((m) => (
                      <Group key={m.membershipId} justify="space-between" wrap="nowrap">
                        <div>
                          <Text size="sm">{memberName(m)}</Text>
                          <Text size="xs" c="dimmed">
                            Asked {m.requestedAt === null ? "" : formatDateOnly(m.requestedAt)}
                          </Text>
                          {m.requestNote === null || m.requestNote === undefined ? null : (
                            <Text size="sm" fs="italic" mt={4}>
                              “{m.requestNote}”
                            </Text>
                          )}
                        </div>
                        {manage ? (
                          <Group gap="xs" wrap="nowrap">
                            <Button
                              size="compact-sm"
                              variant="light"
                              color={semanticColor("success")}
                              loading={
                                decide.isPending && decide.variables.membershipId === m.membershipId
                              }
                              onClick={() => {
                                decide.mutate({
                                  groupId: group.id,
                                  membershipId: m.membershipId,
                                  approve: true,
                                });
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="compact-sm"
                              variant="subtle"
                              color="gray"
                              onClick={() => {
                                decide.mutate({
                                  groupId: group.id,
                                  membershipId: m.membershipId,
                                  approve: false,
                                });
                              }}
                            >
                              Decline
                            </Button>
                          </Group>
                        ) : null}
                      </Group>
                    ))}
                  </Stack>
                ) : null}

                <Stack gap="xs">
                  {leaders.length + active.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      Nobody in this season yet.
                    </Text>
                  ) : null}
                  {[...leaders, ...active].map((m) => (
                    <Group key={m.membershipId} justify="space-between" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm">{memberName(m)}</Text>
                        {m.role === "leader" ? (
                          <Badge size="sm" variant="light" color="teal">
                            Leader
                          </Badge>
                        ) : null}
                      </Group>
                      {manage ? (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={`Actions for ${memberName(m)}`}
                            >
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              onClick={() => {
                                setRole.mutate({
                                  groupId: group.id,
                                  membershipId: m.membershipId,
                                  role: m.role === "leader" ? "member" : "leader",
                                });
                              }}
                            >
                              {m.role === "leader" ? "Make a member" : "Make a leader"}
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              onClick={() => {
                                setRemoving(m);
                              }}
                            >
                              Remove from this season
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : null}
                    </Group>
                  ))}
                  {rosterData !== undefined && rosterData.childrenHidden > 0 ? (
                    <Text size="xs" c="dimmed">
                      {rosterData.childrenHidden === 1
                        ? "1 child is in this season and not shown"
                        : `${String(rosterData.childrenHidden)} children are in this season and not shown`}
                      ; seeing children's records takes a permission the church grants on purpose.
                    </Text>
                  ) : null}
                </Stack>

                {gone.length > 0 ? (
                  <Text size="xs" c="dimmed">
                    No longer in this season:{" "}
                    {gone
                      .map((m) => `${memberName(m)} (${m.status === "left" ? "left" : "declined"})`)
                      .join(", ")}
                  </Text>
                ) : null}

                {manage && currentSeason !== undefined ? (
                  canPickPeople.data === true ? (
                    <Stack gap="xs">
                      <Divider />
                      <Text size="sm" fw={600}>
                        Add to {currentSeason.name}
                      </Text>
                      <Select
                        aria-label="Person"
                        placeholder="Search by name"
                        searchable
                        searchValue={search}
                        onSearchChange={setSearch}
                        data={options.data ?? []}
                        value={newMember}
                        onChange={setNewMember}
                        nothingFoundMessage={options.isPending ? "Searching" : "Nobody matches"}
                        filter={({ options: all }) => all}
                      />
                      <Group justify="space-between" align="center">
                        <Checkbox
                          label="As a leader"
                          checked={newLeader}
                          onChange={(event) => {
                            setNewLeader(event.currentTarget.checked);
                          }}
                        />
                        <Button
                          size="sm"
                          disabled={newMember === null}
                          loading={addMember.isPending}
                          onClick={() => {
                            if (newMember === null) return;
                            addMember.mutate(
                              {
                                groupId: group.id,
                                seasonId: currentSeason.id,
                                personId: newMember,
                                role: newLeader ? "leader" : "member",
                              },
                              {
                                onSuccess: () => {
                                  setNewMember(null);
                                  setSearch("");
                                  setNewLeader(false);
                                },
                              },
                            );
                          }}
                        >
                          Add
                        </Button>
                      </Group>
                      {addMember.isError ? (
                        <Text size="sm" c="red">
                          Could not add this person. Check your access and try again.
                        </Text>
                      ) : null}
                    </Stack>
                  ) : (
                    <Text size="xs" c="dimmed">
                      Members join from the portal, or the office adds them; approving requests is
                      yours.
                    </Text>
                  )
                ) : null}
              </Stack>
            )}
          </section>
        </Stack>
      )}

      <GroupModal
        key={editing ? `edit-${group?.id ?? ""}` : "closed"}
        group={editing ? group : undefined}
        canManage={canManage.data === true}
        canEdit={manage}
        onClose={() => {
          setEditing(false);
        }}
      />

      <ConfirmActionModal
        opened={removing !== null}
        onClose={() => {
          setRemoving(null);
        }}
        title="Remove from this season"
        message={
          removing === null || group === undefined
            ? ""
            : `Remove ${memberName(removing)} from ${group.name}? They stay in the directory and on the timeline as having left.`
        }
        confirmLabel="Remove"
        danger
        loading={removeMember.isPending}
        onConfirm={() => {
          if (removing === null || group === undefined) return;
          removeMember.mutate(
            { groupId: group.id, membershipId: removing.membershipId },
            {
              onSuccess: () => {
                setRemoving(null);
              },
            },
          );
        }}
      />
    </RecordDrawer>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {value === null ? (
        <Text size="sm" c="dimmed">
          Not set
        </Text>
      ) : (
        <Text size="sm">{value}</Text>
      )}
    </div>
  );
}
